import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, Play, Youtube, Upload, Link, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const match = url.match(p);
    if (match) return match[1];
  }
  return null;
}

export default function AdminVideos() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", duration: "", order: 0 });
  const [uploadMode, setUploadMode] = useState("youtube");

  useEffect(() => { fetchVideos(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const fetchVideos = async () => {
    const { data } = await api.get(`/chapters/${chapterId}/videos`);
    setVideos(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.url) {
      return;
    }
    const payload = { ...form, order: parseInt(form.order) || 0 };
    await api.post(`/chapters/${chapterId}/videos`, payload);
    setShowForm(false);
    setForm({ title: "", url: "", duration: "", order: 0 });
    fetchVideos();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this video?")) {
      await api.delete(`/videos/${id}`);
      fetchVideos();
    }
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-videos-page">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#0284C7] font-bold mb-3">
          <ArrowLeft size={16} /> Back to Chapters
        </button>

        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Videos</h1>
          <button
            data-testid="add-video-btn"
            onClick={() => { setShowForm(!showForm); setUploadMode("youtube"); }}
            className="bg-[#0284C7] text-white font-bold py-2 px-4 text-sm flex items-center gap-2 transition-none"
          >
            <Plus size={16} /> Add
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-4 mb-4 space-y-4" data-testid="video-form">
            <input data-testid="video-title-input" placeholder="Video Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />

            {/* Upload Mode Toggle */}
            <div className="flex gap-2" data-testid="upload-mode-toggle">
              <button
                type="button"
                onClick={() => setUploadMode("youtube")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-none ${
                  uploadMode === "youtube" ? "bg-red-500 text-white" : "admin-card border admin-text"
                }`}
              >
                <Youtube size={14} /> YouTube URL
              </button>
              <button
                type="button"
                onClick={() => setUploadMode("link")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-none ${
                  uploadMode === "link" ? "bg-[#0284C7] text-white" : "admin-card border admin-text"
                }`}
              >
                <Link size={14} /> Video Link
              </button>
              <button
                type="button"
                onClick={() => setUploadMode("upload")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-none ${
                  uploadMode === "upload" ? "bg-green-600 text-white" : "admin-card border admin-text"
                }`}
              >
                <Upload size={14} /> Upload File
              </button>
            </div>

            {/* YouTube URL Mode (Recommended) */}
            {uploadMode === "youtube" && (
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-100 p-4">
                  <h3 className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                    <Youtube size={14} /> How to add YouTube Video
                  </h3>
                  <ol className="text-[11px] text-red-600 space-y-1.5 list-decimal list-inside">
                    <li>Open <strong>YouTube</strong> app or website</li>
                    <li>Upload your video (set as <strong>"Unlisted"</strong> for privacy)</li>
                    <li>Copy the video link</li>
                    <li>Paste it below</li>
                  </ol>
                </div>
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">YouTube Video URL</label>
                  <input
                    data-testid="video-url-input"
                    placeholder="https://youtu.be/XXXXX or https://youtube.com/watch?v=XXXXX"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    className={inputClass}
                  />
                </div>
                {/* YouTube Preview */}
                {extractYouTubeId(form.url) && (
                  <div className="aspect-video w-full max-w-md bg-black overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${extractYouTubeId(form.url)}`}
                      title="Preview"
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}

            {/* Direct Link Mode */}
            {uploadMode === "link" && (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Video URL (MP4 / Direct Link)</label>
                  <input
                    data-testid="video-url-input"
                    placeholder="https://example.com/video.mp4"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    className={inputClass}
                  />
                  <p className="text-[10px] admin-text-secondary mt-1">Paste any direct video link (MP4, Google Drive, Dropbox, etc.)</p>
                </div>
              </div>
            )}

            {/* File Upload Mode */}
            {uploadMode === "upload" && (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 p-3 flex gap-2">
                  <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-700">Max 50MB for direct upload</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">For larger videos, use <strong>YouTube URL</strong> method (recommended). Upload your video to YouTube first, then paste the link.</p>
                  </div>
                </div>
                <FileUpload
                  label="Upload Video (Max 50MB)"
                  accept="video/mp4,video/webm,video/mov"
                  uploadType="videos"
                  currentUrl={form.url}
                  onUpload={(url) => { if (url) setForm({ ...form, url: url }); }}
                />
              </div>
            )}

            {/* Duration & Order */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Duration</label>
                <input data-testid="video-duration-input" placeholder="e.g. 1:30:00" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Order</label>
                <input data-testid="video-order-input" placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={inputClass} />
              </div>
            </div>

            {/* Submit */}
            <button data-testid="video-submit-btn" type="submit" disabled={!form.url || !form.title} className="bg-[#0284C7] text-white font-bold py-3 px-6 text-sm transition-none disabled:opacity-40 w-full">
              Add Video
            </button>
          </form>
        )}

        <div className="space-y-2" data-testid="videos-list">
          {videos.map((video) => {
            const ytId = extractYouTubeId(video.url);
            return (
              <div key={video.id} className="admin-card border p-3 flex items-center gap-3">
                <div className="w-16 aspect-video bg-slate-800 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {ytId ? (
                    <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Play size={14} className="text-[#0284C7]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold admin-text truncate">{video.title}</p>
                  <p className="text-xs admin-text-secondary">{video.duration || "—"} &middot; Order: {video.order}</p>
                </div>
                <button onClick={() => handleDelete(video.id)} className="p-2 text-red-500">
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
          {videos.length === 0 && <p className="text-sm admin-text-secondary py-4">No videos yet. Add one above.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
